import { NgClass, NgIf } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  Output,
  runInInjectionContext,
  Signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormControlStatus,
  FormsModule,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty, isObject } from 'lodash';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { UserEffects } from '../../../state/user/user.effects';
import { DATE_FORMAT, datePickerRangeOptions, dateRangeOptionValues } from '../../../utilities/date-utilities.service';
import { CustomMatErrorComponent } from '../custom-mat-error/custom-mat-error.component';
import { IDateRangePickerConfiguration, IDateRangePickerDate } from './date-range-picker.interface';
import { DaterangepickerConfigModule } from './date-range-picker.module';

@Component({
  imports: [
    FormsModule,
    DaterangepickerConfigModule,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    NgClass,
    NgIf,
    CustomMatErrorComponent,
    MatIcon,
  ],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
    },
  ],
  selector: 'app-date-range-picker',
  styleUrls: ['./date-range-picker.component.scss'],
  templateUrl: './date-range-picker.component.html',
})
export class DateRangePickerComponent implements ControlValueAccessor, OnInit {
  @Input() public formControl: FormControl<IDateRangePickerDate | null | undefined> = new FormControl<
    IDateRangePickerDate | null | undefined
  >(undefined);
  @Input() public isFormSubmitted = false;
  public dates: ModelSignal<IDateRangePickerDate | undefined> = model<IDateRangePickerDate | undefined>();
  public configurationInput: InputSignal<IDateRangePickerConfiguration | undefined> = input<
    IDateRangePickerConfiguration | undefined
  >({} as IDateRangePickerConfiguration);
  @Input() public ranges: { [key: string]: [Dayjs, Dayjs] } = datePickerRangeOptions.reduce(
    (result, rangeOption) => ({
      ...result,
      [this.translate.instant(`system.label.dateRanges.${rangeOption}`)]: dateRangeOptionValues[rangeOption](),
    }),
    {},
  );

  @Output() datesChangedByCalendar: EventEmitter<IDateRangePickerDate> = new EventEmitter<IDateRangePickerDate>();

  @ViewChild(DaterangepickerDirective, { static: false }) pickerDirective!: DaterangepickerDirective;

  public userDateFormat!: string;
  public readonly firstDayOfWeekIndex!: number;
  // (Improvement) this can be converted to a signal
  public configuration!: IDateRangePickerConfiguration;
  public selectedDates?: IDateRangePickerDate;
  public isTouched = false;
  public readonly minDate = computed(() => this.configurationInput()?.minDate ?? dayjs.unix(0));

  private readonly defaultConfiguration: IDateRangePickerConfiguration = {
    drops: 'down',
    isTimePickerShown: true,
    opens: 'right',
    showBackwardForward: false,
    showClearButton: false,
    showErrors: true,
    showRanges: false,
    singleDatePicker: false,
    subscriptSizing: 'fixed',
  };

  private statusChanges: Signal<FormControlStatus | undefined> = computed(() => 'VALID');

  onChange: (value: IDateRangePickerDate | undefined) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private readonly injector: Injector,
    private readonly translate: TranslateService,
  ) {
    const dateFormat: string = dayjs()
      .localeData()
      .longDateFormat(`${UserEffects.getUser().configuration.dateFormat}` as 'LL' | 'L');
    const timeFormat = 'LT';

    this.firstDayOfWeekIndex = dayjs.tz().weekday(0).get('d');

    this.configuration = { ...this.defaultConfiguration, ...this.configurationInput() };
    this.userDateFormat = this.configuration.isTimePickerShown ? `${dateFormat} ${timeFormat}` : dateFormat;

    this.selectedDates = this.dates();

    effect(() => {
      if (this.dates()?.startDate && this.dates()?.endDate) {
        this.selectedDates = this.dates();
      }

      if (this.configurationInput()) {
        this.configuration = { ...this.defaultConfiguration, ...this.configurationInput() };
        this.userDateFormat = this.configuration.isTimePickerShown ? `${dateFormat} ${timeFormat}` : dateFormat;
        this.statusChanges();

        this.configuration.label =
          this.formControl?.status !== 'DISABLED' && this.formControl.hasValidator(Validators.required)
            ? `${this.configurationInput()?.label}*`
            : this.configurationInput()?.label;

        if (!this.selectedDates?.startDate && !this.selectedDates?.endDate) {
          this.clearDates();
        }
      }

      this.displayDateAsSingleDate();
    });
  }

  public ngOnInit(): void {
    if (this.formControl) {
      runInInjectionContext(this.injector, () => {
        this.statusChanges = toSignal(this.formControl.statusChanges);
      });
    }
  }

  public onDatesUpdated(range: unknown): void {
    const modifiedRange: IDateRangePickerDate | undefined = range as IDateRangePickerDate;

    if (!modifiedRange?.endDate || !modifiedRange?.startDate) {
      return;
    }

    this.selectedDates = {
      endDate: dayjs.tz((range as IDateRangePickerDate).endDate!.format(DATE_FORMAT)),
      startDate: dayjs.tz((range as IDateRangePickerDate).startDate!.format(DATE_FORMAT)),
    };

    this.dates.set(this.selectedDates);
    this.onChange(this.selectedDates);
    this.datesChangedByCalendar.emit(this.selectedDates);
  }

  public openDatepicker(): void {
    this.pickerDirective.open();
    this.markAsTouched();
    this.handleInDialogPositioning();
  }

  public clearDates(): void {
    if (!this.configuration.showClearButton) {
      return;
    }

    this.dates.set(undefined);
    this.selectedDates = undefined;
    const stayAsPristine = !this.formControl.value && this.formControl.pristine;
    this.onChange(undefined);

    if (stayAsPristine) {
      this.formControl.markAsPristine();
    }

    this.datesChangedByCalendar.emit(undefined);
  }

  public writeValue(value: IDateRangePickerDate): void {
    if (!isObject(value) || !isEmpty(value)) {
      this.selectedDates = value;
    }
  }

  public markAsTouched(): void {
    this.onTouched();
    this.isTouched = true;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public changeForward(): void {
    this.shiftDateRange('forward');
  }

  public changeBackward(): void {
    this.shiftDateRange('backward');
  }

  private shiftDateRange(direction: 'forward' | 'backward'): void {
    const { startDate, endDate } = this.selectedDates || {};

    if (!startDate || !endDate) {
      return;
    }

    const shiftMethod: 'add' | 'subtract' = direction === 'forward' ? 'add' : 'subtract';
    const isFullMonth: boolean = startDate.date() === 1 && endDate.isSame(endDate.clone().endOf('month'), 'day');
    const isFullYear: boolean =
      startDate.isSame(startDate.clone().startOf('year'), 'day') &&
      endDate.isSame(endDate.clone().endOf('year'), 'day');

    const diffInDays: number = Math.max(1, endDate.diff(startDate, 'day', true));
    let newStartDate: Dayjs = startDate.clone()[shiftMethod](diffInDays, 'day');
    let newEndDate: Dayjs = endDate.clone()[shiftMethod](diffInDays, 'day');

    if (isFullYear) {
      newStartDate = startDate.clone()[shiftMethod](1, 'year').startOf('year');
      newEndDate = endDate.clone()[shiftMethod](1, 'year').endOf('year');
    } else if (isFullMonth) {
      newStartDate = startDate.clone()[shiftMethod](1, 'month').startOf('month');
      newEndDate = endDate.clone()[shiftMethod](1, 'month').endOf('month');
    }

    this.selectedDates = { endDate: newEndDate, startDate: newStartDate };
    this.formControl.setValue(this.selectedDates);
  }

  private displayDateAsSingleDate(): void {
    if (this.configuration.singleDatePicker && this.selectedDates?.startDate && !this.isTouched) {
      setTimeout(() => {
        this.selectedDates = {
          endDate: this.selectedDates!.endDate,
          startDate: this.selectedDates!.startDate,
        } as IDateRangePickerDate;
      });
    }
  }

  private handleInDialogPositioning(): void {
    setTimeout(() => {
      if (!this.pickerDirective?.picker?.pickerContainer?.nativeElement) {
        return;
      }

      if (this.configuration.forceFit) {
        const topValue: number = parseFloat(this.pickerDirective.picker.pickerContainer.nativeElement.style.top);
        const calculatedTopValue: number =
          Math.sign(topValue) *
          (Math.abs(topValue) - (this.pickerDirective.picker.pickerContainer.nativeElement?.clientHeight ?? 0) / 2);
        this.pickerDirective.picker.pickerContainer.nativeElement.style.top = `${calculatedTopValue}px`;
      }

      if (this.configuration.zoom) {
        this.pickerDirective.picker.pickerContainer.nativeElement.style.zoom = `${this.configuration.zoom}`;
      }
    });
  }
}
